import {
  defaultDataIdFromObject,
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory'
import { ApolloClient } from 'apollo-client'
import { ApolloLink, Observable } from 'apollo-link'
import { setContext } from 'apollo-link-context'
import { onError } from 'apollo-link-error'
import { RetryLink } from 'apollo-link-retry'
import { createUploadLink } from 'apollo-upload-client'
import { omitDeepRegex } from 'helpers'
import { getToken } from './auth'
import introspectionQueryResultData from './graphqlFragments.generated.json'
export { default as gql } from 'graphql-tag'

const FALLBACK_URI = 'http://localhost:4000/graphql'

const uri = `${process.env.REACT_APP_GRAPHQL_ENDPOINT || FALLBACK_URI}`

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
})
const promiseToObservable = (promise: Promise<any>) => {
  return new Observable(
    (subscriber: {
      closed: any
      next: (arg0: any) => void
      complete: () => void
      error: (arg0: any) => void
    }) => {
      promise.then(
        (value) => {
          if (subscriber.closed) {
            return
          }
          subscriber.next(value)
          subscriber.complete()
        },
        (err) => {
          subscriber.error(err)
        }
      )
    }
  )
}
let cachedToken: string | null

const withToken = setContext((request) => {
  if (cachedToken) {
    return { headers: { authorization: `Bearer ${cachedToken}` } }
  }

  return new Promise(async (success, fail) => {
    cachedToken = await getToken()
    success({ headers: { authorization: `Bearer ${cachedToken}` } })
  })
})

const resetToken = onError(({ networkError, operation, forward }: any) => {
  if (networkError && networkError.statusCode === 401) {
    const oldHeaders = operation.getContext().headers
    const promise = getToken()
    return promiseToObservable(promise).flatMap((newToken) => {
      cachedToken = newToken
      operation.setContext({
        headers: {
          ...oldHeaders,
          authorization: `Bearer ${newToken}`,
        },
      })
      // retry the request, returning the new observable
      return forward(operation)
    })
  }
  return
})

const authFlowLink = withToken.concat(resetToken)

const sanitizeVariablesLink = new ApolloLink((operation, forward) => {
  if (
    operation.operationName !== 'UploadBlob' &&
    operation.operationName !== 'CreateWiki' &&
    operation.operationName !== 'UpdateWiki' &&
    operation.operationName !== 'CreateNews' &&
    operation.operationName !== 'UpdateNews'
  ) {
    operation.variables = operation.variables
      ? omitDeepRegex(operation.variables, /^\$\w+/)
      : operation.variables
  }

  return forward!(operation)
})

export function createApolloClient() {
  const cache = new InMemoryCache({
    fragmentMatcher,
    dataIdFromObject: (object) => {
      // quick and dirty fix to never cache User as each user will have a unique Caching ID.
      switch (object.__typename) {
        case 'User':
          return String(Math.random())
        default:
          return defaultDataIdFromObject(object)
      }
    },
  })

  return new ApolloClient({
    cache,
    link: ApolloLink.from([
      new RetryLink({
        delay: {
          initial: 500,
          max: Infinity,
          jitter: true,
        },
        attempts: {
          max: 10,
          retryIf: (error) => error.statusCode === 500,
        },
      }),
      authFlowLink,
      sanitizeVariablesLink,
      createUploadLink({ uri }),
    ]),
  })
}
